import { resendVerification } from "api/auth";
import { ApiError } from "api/client";
import { uploadImage } from "api/images";
import { getSubscriptionStatus } from "api/subscriptions";
import { createTree, getMyTrees } from "api/trees";
import type { CollaboratorAvatarDto, SubscriptionStatusDto, TreeSummaryDto } from "api/types";
import React, { type FormEvent, useCallback, useDeferredValue, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Paths, { getTreeEditPath } from "routes/Paths";
import { useSession } from "Session";
import { compressImage, formatLastModified } from "utilities";

import { CreateTreeDialog, HomeSidebar, TreeLibrary, TreeToolbar, VerifyEmailBanner } from "./components";
import { createTreeInitialForm, defaultCoverPreview } from "./data";

import type { CardAvatar, NewTreeForm, Status, TreeCard, TreeFilter } from "./models";

import styles from "./HomePage.module.css";

const getInitials = (name: string): string =>
	name
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((word: string) => word[0]?.toUpperCase() ?? "")
		.join("") || "U";

const getTreeStatus = (visibility: string): Status => {
	if (visibility === "Draft" || visibility === "Private" || visibility === "Public") {
		return visibility;
	}

	return "Private";
};

const HomePage = () => {
	const { t } = useTranslation("homePage");
	const navigate = useNavigate();
	const { getAccessToken, hasSession, signOut, user } = useSession();
	const [resendState, setResendState] = useState<"idle" | "sending" | "sent" | "blocked">("idle");
	const [resendError, setResendError] = useState("");
	const [cards, setCards] = useState<TreeCard[]>([]);
	const [searchValue, setSearchValue] = useState("");
	const [activeFilter, setActiveFilter] = useState<TreeFilter>("All");
	const [isLoadingTrees, setIsLoadingTrees] = useState(false);
	const [treeLoadError, setTreeLoadError] = useState("");
	const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
	const [createTreeError, setCreateTreeError] = useState("");
	const [isCreatingTree, setIsCreatingTree] = useState(false);
	const [newTreeForm, setNewTreeForm] = useState<NewTreeForm>(createTreeInitialForm);
	const [coverPreview, setCoverPreview] = useState(defaultCoverPreview);
	const [coverFile, setCoverFile] = useState<File | null>(null);
	const [subscription, setSubscription] = useState<SubscriptionStatusDto | null>(null);
	const deferredSearchValue = useDeferredValue(searchValue.trim());

	const isVerified = user?.emailConfirmed ?? false;
	const displayName = user?.fullName ?? t("common:yourAccount");
	const initials = getInitials(user?.fullName ?? "");

	// Stable card-avatar fallback for trees without collaborator data (memoized so it doesn't retrigger loads).
	const viewerAvatar = useMemo<CardAvatar>(
		() => ({
			imageUrl: user?.profileImageUrl ?? null,
			initials: getInitials(user?.fullName ?? ""),
			name: user?.fullName ?? t("common:yourAccount")
		}),
		[t, user?.fullName, user?.profileImageUrl]
	);
	const canCreatePrivateTrees = subscription?.canCreatePrivateTrees ?? false;
	const isPro = subscription?.isPro ?? false;

	useEffect(() => {
		if (!hasSession) {
			setCards([]);

			return undefined;
		}

		let isCurrent = true;

		const loadTrees = async (): Promise<void> => {
			setIsLoadingTrees(true);
			setTreeLoadError("");

			try {
				const token = await getAccessToken();
				if (!token || !isCurrent) {
					return;
				}

				const response = await getMyTrees(
					{
						pageIndex: 1,
						pageSize: 24,
						search: deferredSearchValue || undefined,
						visibility: activeFilter === "All" ? undefined : activeFilter
					},
					token
				);

				if (!isCurrent) {
					return;
				}

				setCards(
					response.items.map((tree: TreeSummaryDto) => ({
						// Owner + active Editors/Admins; falls back to the viewer's own avatar.
						avatars:
							tree.collaborators && tree.collaborators.length > 0
								? tree.collaborators.slice(0, 4).map((collaborator: CollaboratorAvatarDto) => ({
										imageUrl: collaborator.profileImageUrl,
										initials: getInitials(collaborator.fullName),
										name: collaborator.fullName
								  }))
								: [viewerAvatar],
						cover: tree.coverImageUrl?.trim() || defaultCoverPreview,
						description: tree.description?.trim() || t("library.noDescription"),
						entityCount: tree.entityCount,
						id: tree.id,
						isLiked: tree.isLikedByMe,
						lastEditedLabel: formatLastModified(tree.lastModifiedDate),
						likes: tree.likeCount,
						status: getTreeStatus(tree.visibility),
						title: tree.title
					}))
				);
			} catch (error) {
				if (!isCurrent) {
					return;
				}

				setCards([]);
				setTreeLoadError(error instanceof Error ? error.message : t("library.loadError"));
			} finally {
				if (isCurrent) {
					setIsLoadingTrees(false);
				}
			}
		};

		loadTrees().catch(() => undefined);

		return () => {
			isCurrent = false;
		};
	}, [activeFilter, deferredSearchValue, getAccessToken, hasSession, t, viewerAvatar]);

	// Pro entitlement gates the Private visibility option and drives the sidebar upgrade button.
	useEffect(() => {
		if (!hasSession) {
			setSubscription(null);

			return undefined;
		}

		let isCurrent = true;

		const loadSubscription = async (): Promise<void> => {
			try {
				const token = await getAccessToken();
				if (!token || !isCurrent) {
					return;
				}

				const status = await getSubscriptionStatus(token);
				if (isCurrent) {
					setSubscription(status);
				}
			} catch {
				// Keep Pro perks locked when the status lookup fails.
			}
		};

		loadSubscription().catch(() => undefined);

		return () => {
			isCurrent = false;
		};
	}, [getAccessToken, hasSession]);

	// Upgrading starts on the plans page (plan overview + billing details before checkout).
	const onUpgradeToPro = useCallback((): void => navigate(Paths.plans), [navigate]);

	const onResend = useCallback(async (): Promise<void> => {
		const token = await getAccessToken();
		if (!token) {
			return;
		}

		setResendState("sending");
		setResendError("");
		try {
			await resendVerification(token);
			setResendState("sent");
		} catch (error) {
			// A 400 here is the daily resend cap: keep the button disabled and show why.
			const isThrottled = error instanceof ApiError && error.status === 400;

			setResendError(isThrottled ? error.message : t("verification.resendError"));
			setResendState(isThrottled ? "blocked" : "idle");
		}
	}, [getAccessToken, t]);

	const openCreateDialog = useCallback((): void => {
		// Private is a Pro perk, so free accounts start from Draft instead of the Private default.
		setNewTreeForm(
			canCreatePrivateTrees ? createTreeInitialForm : { ...createTreeInitialForm, visibility: "Draft" }
		);
		setCoverPreview(defaultCoverPreview);
		setCoverFile(null);
		setCreateTreeError("");
		setIsCreateDialogOpen(true);
	}, [canCreatePrivateTrees]);

	const closeCreateDialog = useCallback((): void => {
		setIsCreateDialogOpen(false);
	}, []);

	const updateCreateTreeForm = useCallback((updates: Partial<NewTreeForm>): void => {
		setNewTreeForm((current: NewTreeForm) => ({ ...current, ...updates }));
	}, []);

	const onCoverSelect = useCallback((file: File): void => {
		setCoverFile(file);

		const onReaderLoad = (): void => {
			if (typeof reader.result === "string") {
				setCoverPreview(reader.result);
			}
		};

		const reader = new FileReader();
		reader.onload = onReaderLoad;
		reader.readAsDataURL(file);
	}, []);

	const onCreateTree = useCallback(
		async (event: FormEvent<HTMLFormElement>): Promise<void> => {
			event.preventDefault();

			const title = newTreeForm.title.trim();
			if (!title) {
				return;
			}

			const token = await getAccessToken();
			if (!token) {
				return;
			}

			setCreateTreeError("");
			setIsCreatingTree(true);

			try {
				let coverImageUrl: string | undefined;
				let coverImageName: string | undefined;
				if (coverFile) {
					// The cover is optional — if the upload fails (e.g. blob storage/Azurite down), still create
					// the tree rather than blocking creation. The cover can be added later.
					try {
						// Same treatment as node images: re-encoding through a canvas resizes the cover and
						// drops the Exif block with it, so a family photo does not publish the coordinates it
						// was taken at on a tree anyone can open.
						const uploaded = await uploadImage(await compressImage(coverFile), "trees", token);
						coverImageUrl = uploaded.url;
						coverImageName = uploaded.name;
					} catch {
						// Intentionally ignored: proceed to create the tree without a cover image.
					}
				}

				const createdTree = await createTree(
					{
						title,
						description: newTreeForm.description.trim() || undefined,
						visibility: newTreeForm.visibility,
						commentsEnabled: newTreeForm.commentsWhenPublic,
						coverImageUrl,
						coverImageName
					},
					token
				);

				setCards((current: TreeCard[]) => [
					{
						avatars: [viewerAvatar],
						cover: createdTree.coverImageUrl?.trim() || coverPreview,
						description: createdTree.description?.trim() || t("library.noDescription"),
						entityCount: createdTree.entityCount,
						id: createdTree.id,
						isLiked: createdTree.isLikedByMe,
						lastEditedLabel: formatLastModified(createdTree.lastModifiedDate),
						likes: createdTree.likeCount,
						status: getTreeStatus(createdTree.visibility),
						title: createdTree.title
					},
					...current
				]);
				setIsCreateDialogOpen(false);
				navigate(getTreeEditPath(createdTree.id));
			} catch (error) {
				setCreateTreeError(
					error instanceof Error ? error.message : "Unable to create your tree. Please try again."
				);
			} finally {
				setIsCreatingTree(false);
			}
		},
		[coverFile, coverPreview, getAccessToken, navigate, newTreeForm, t, viewerAvatar]
	);

	let libraryMessage = "";

	if (treeLoadError) {
		libraryMessage = treeLoadError;
	} else if (cards.length === 0) {
		if (isLoadingTrees) {
			libraryMessage = t("library.loading");
		} else if (deferredSearchValue || activeFilter !== "All") {
			libraryMessage = t("library.emptyFiltered");
		} else {
			libraryMessage = t("library.empty");
		}
	}

	return (
		<div className={styles.shell}>
			<HomeSidebar
				displayName={displayName}
				initials={initials}
				isPro={isPro}
				onSignOut={signOut}
				onUpgrade={onUpgradeToPro}
				profileImageUrl={user?.profileImageUrl}
			/>

			<main className={styles.main}>
				{user && !isVerified && (
					<VerifyEmailBanner
						email={user.email ?? ""}
						onResend={onResend}
						resendError={resendError}
						resendState={resendState}
					/>
				)}

				<TreeToolbar
					activeFilter={activeFilter}
					isVerified={isVerified}
					onCreateTree={openCreateDialog}
					onFilterChange={setActiveFilter}
					onSearchChange={setSearchValue}
					searchValue={searchValue}
				/>
				{libraryMessage ? (
					<p className={styles.libraryMessage}>{libraryMessage}</p>
				) : (
					<TreeLibrary cards={cards} />
				)}
			</main>

			{isCreateDialogOpen && (
				<CreateTreeDialog
					canCreatePrivateTrees={canCreatePrivateTrees}
					coverPreview={coverPreview}
					errorMessage={createTreeError}
					form={newTreeForm}
					isSubmitting={isCreatingTree}
					onClose={closeCreateDialog}
					onCoverSelect={onCoverSelect}
					onFormChange={updateCreateTreeForm}
					onSubmit={onCreateTree}
				/>
			)}
		</div>
	);
};

export default HomePage;
